import { useEffect, useRef, useState } from 'react';

import { useMeasuredBar } from '../measure';

/** How long a thing that has already happened can still be taken back. */
const LIVE_MS = 5000;
/** And how long it takes to leave, which is what everything else leaves in. */
const OUT_MS = 180;

/**
 * Something that has been done, and the way back from it.
 *
 * `id` is bumped for every action so that a second one restarts the five
 * seconds rather than inheriting what is left of the first one's.
 */
export type Done = {
  id: number;
  /** What happened, in the past tense and without an exclamation mark. */
  text: string;
  /** Putting it back. This really is a call to the server, not a decision not to act. */
  undo: () => void;
};

/**
 * How a card tells the screen it stands on that something reversible happened.
 *
 * The bar belongs to the screen and not to the card: «Хор» carries two cards
 * that both do reversible things, and one bar between them is the difference
 * between one report and two stacked on top of each other.
 */
export type Report = (text: string, undo: () => void) => void;

/**
 * The third container: what has already happened, and one word to undo it.
 *
 * It replaces a question rather than joining one — if the bar is up, nothing
 * was asked. Which is the point of the rule it belongs to: «вы уверены?» is put
 * before the person has seen anything, and «Отменить» after they have. For
 * anything that can be taken back with the same screen in ten seconds the
 * second is the honest one, and the first was only ever there because it is
 * cheaper to write.
 *
 * One bar and never two. A second action replaces what is on this one and
 * restarts the count: two bars stacked would put the older one where the tab
 * bar is, and one of the two «Отменить» would undo something the person had
 * stopped thinking about.
 *
 * It stands above the tab bar and clear of the «Запланировать» capsule, and it
 * covers nothing — but not by itself. That was said of a list whose foot is
 * 16px of padding, and 16 is less than the bar is tall: at the end of a scroll
 * it sat on the last line it stood over, which on a person's card is the hint
 * under «Удалить из хора». So the bar measures itself into `--undo` and the
 * shell ends above it, the way a form ends above its `Dock`.
 */
export default function UndoBar({ done, onGone }: { done: Done | null; onGone: () => void }) {
  /** What is on the bar. Held after `done` clears, so the bar can drive off. */
  const [held, setHeld] = useState<Done | null>(done);
  /** Whether it is on its way out. */
  const [going, setGoing] = useState(false);

  // Read inside a timeout set once, so the caller may hand over a new closure
  // on every render without restarting the five seconds.
  const gone = useRef(onGone);
  gone.current = onGone;

  useEffect(() => {
    if (done === null) return;
    setHeld(done);
    setGoing(false);
    const life = window.setTimeout(() => setGoing(true), LIVE_MS);
    return () => window.clearTimeout(life);
  }, [done]);

  useEffect(() => {
    if (!going) return;
    const off = window.setTimeout(() => {
      setHeld(null);
      setGoing(false);
      gone.current();
    }, OUT_MS);
    return () => window.clearTimeout(off);
  }, [going]);

  // The shell has to end above the bar, and the bar is sized by its sentence -
  // so it is measured the way `Dock` measures itself rather than declared the
  // way `--panel` is. Layout rather than effect: an effect would leave the page
  // padded for nothing for the frame the bar arrives in, which is the frame a
  // person scrolled to the bottom would see the last line jump in.
  /** The bar itself, so the shell can be told how much room to leave for it.
      Its own sentence sizes it - «Андрей Лебедев теперь регент» is one line
      where a longer name is two - and the callback fires when the bar arrives
      and again with null when it goes, which is the life of a bar that stands
      for five seconds. */
  const box = useMeasuredBar('--undo');

  if (held === null) return null;

  return (
    // Arriving is a keyframe on the class and leaving is a transition on the
    // modifier, rather than a pair of either: the bar has to be off screen for
    // the frame it mounts in, and a transition has nothing to run from then.
    <div className={going ? 'undo-bar undo-bar--going' : 'undo-bar'} role="status" ref={box}>
      <span className="undo-what">{held.text}</span>
      {/* One word, and it is the only thing here that can be pressed: a bar
          with two controls on it is a question again. */}
      <button
        className="undo-back"
        onClick={() => {
          held.undo();
          setGoing(true);
        }}
      >
        Отменить
      </button>
    </div>
  );
}
